import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { isAuthenticated, getUser, logout as authLogout } from '../services/authApi';
import { clearAllLocalStorage } from '../utils/clearAllLocalStorage';

const AuthContext = createContext(null);

/**
 * Auth Context Provider
 * Manages authentication state and provides auth methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  // Initialize auth state on mount - only once
  useEffect(() => {
    if (initializedRef.current) return;
    
    const initAuth = () => {
      try {
        if (isAuthenticated()) {
          const userData = getUser();
          if (userData) {
            setUser(userData);
          } else {
            // Token exists but user data is missing - clear token
            authLogout();
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        authLogout();
        setUser(null);
      } finally {
        setLoading(false);
        initializedRef.current = true;
      }
    };

    initAuth();
  }, []);

  const login = (userData) => {
    if (userData) {
      setUser(userData);
    } else {
      // If userData not provided, fetch from localStorage
      const storedUser = getUser();
      setUser(storedUser);
    }
  };

  const logout = () => {
    clearAllLocalStorage();
    authLogout();
    setUser(null);
    // Navigation will be handled by the component calling logout
  };

  const checkAuth = () => {
    try {
      if (isAuthenticated()) {
        const userData = getUser();
        if (userData) {
          setUser(userData);
          return true;
        } else {
          // Token exists but user data is missing
          authLogout();
          setUser(null);
          return false;
        }
      }
      setUser(null);
      return false;
    } catch (error) {
      console.error('Error checking auth:', error);
      authLogout();
      setUser(null);
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: user ? (user.employee_id === 'VACSPL033' || user.is_admin === true || user.is_super_admin === true) : false,
    isSuperAdmin: user ? (user.employee_id === 'VACSPL033' || user.is_super_admin === true) : false,
    login,
    logout,
    checkAuth,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
